import React, { useEffect, useRef, useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import { makeStyles } from "@mui/styles";
import { alpha, Autocomplete, InputAdornment, TextField, useMediaQuery } from "@mui/material";
import { Clear, Search } from "@mui/icons-material";

/** Redux */
import { useDispatch, useSelector } from "react-redux";
import { getShowIDs } from "../../../_appStore/Common";
import { setExpanded, setScrolled } from "../../../_appStore/DB_Indicators";

/** Helpers */
import _ from "lodash";
import clsx from "clsx";
import { matchSorter } from "match-sorter";
import { useTranslation } from "react-i18next";
import useHelper from "../../../_common/useHelper";
import { clean as removeDiacritics } from "diacritic";

/** UI */
const AppTooltip = dynamic(() => import("../../../_common/AppTooltip"));

const useStyles = makeStyles((theme) => ({
    listElement: {
        "& sup": {
            fontSize: "50%",
            marginRight: theme.spacing(0.5),
        },
    },
    TextFieldRoot: {
        "& > input": {
            marginLeft: 32,
        },
        "& > label": {
            [theme.breakpoints.up("md")]: {
                fontSize: "1rem",
            },
            "&:not(.MuiInputLabel-shrink)": {
                fontSize: "1.1111111rem",
                paddingLeft: 32,
                [theme.breakpoints.down("lg")]: {
                    fontSize: "0.88888889rem",
                    paddingTop: 2,
                },
            },
        },
    },
    outlinedInput: {
        fontSize: "1.1111111rem",
        [theme.breakpoints.down("md")]: {
            "& input": {
                fontSize: "0.88888889rem",
            },
        },
    },
    input: {
        opacity: 1,
    },
    inputFocused: {
        opacity: 1,
        marginLeft: 32,
    },
    inputFocusedWithTxt: {
        opacity: 1,
        marginLeft: 0,
    },
    endAdornment: {
        left: 9,
        "& button": {
            transform: "none",
        },
        "& button.MuiAutocomplete-clearIndicator": {
            position: "absolute",
            right: 9,
            top: -16,
            visibility: "visible",
            outlineStyle: "solid",
            outlineOffset: 0,
            "& svg": {
                outline: "none",
            },
        },
    },
    unHighlighted: {
        fontWeight: 400,
    },
    highlighted: {
        fontWeight: 700,
    },
    listbox: {
        padding: theme.spacing(1, 0.5),
        [theme.breakpoints.down("md")]: {
            padding: theme.spacing(1, 0.5, 0, 0.5),
        },
    },
    option: {
        "&, &.MuiAutocomplete-option": {
            "&.MuiAutocomplete-option.Mui-focused": {
                ...theme.outline,
                outlineColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                color: theme.palette.primary.dark,
            },
            [theme.breakpoints.down("md")]: {
                fontSize: "0.88888889rem",
                padding: theme.spacing(0.5, 1),
                minHeight: 32,
                "& + &": {
                    borderTop: "1px solid rgba(0, 0, 0, 0.1)",
                },
            },
        },
    },
    focused: {
        opacity: 1,
    },
}));

const SearchTree = (props) => {
    const { searchData, onSearch } = props;

    const classes = useStyles();
    const { translationFromJson } = useHelper();
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const inputRef = useRef();
    const lgDown = useMediaQuery((theme) => theme.breakpoints.down("lg"));

    /** States */
    const [IN_TXT, setIN_TXT] = useState("");
    const [VALUES, setVALUES] = useState(null);
    /** - redux */
    const showIDs = useSelector(getShowIDs);

    /** Effects */
    /** - set list */
    useEffect(() => {
        const _sorted = JSON.parse(JSON.stringify(searchData.filter((f) => f.type.toUpperCase() === "INDICATOR")))
            .map((s) => ({
                ...s,
                name: s.name.replace(/(<([^>]+)>)|\t|\n/gi, ""),
                name_en: s.name_en.replace(/(<([^>]+)>)|\t|\n/gi, ""),
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
        if (JSON.stringify(VALUES) !== JSON.stringify(_sorted)) {
            setVALUES(_sorted);
        }
    }, [VALUES, searchData]);

    /** Handlers */
    const handleOnChange = ({ event, newValue, reason }) => {
        if (reason === "selectOption" && newValue) {
            let newExpanded = [];

            [...newValue.parrent_id.split("-"), newValue.parrent_id].forEach((v, i) => {
                newExpanded.push(
                    i === 0 ? `${v}` : [...new Set((newExpanded.join("-") + "-" + v).split("-"))].join("-")
                );
            });
            newExpanded = _.uniq(newExpanded);

            dispatch(setExpanded(newExpanded));
            dispatch(setScrolled(newValue.id));
            handleClear();
            setVALUES(null);
        }
        if (reason === "clear") {
            handleClear();
        }
    };

    const handleClear = () => {
        onSearch({ ids: [] });
        setIN_TXT("");
    };

    const handleOnInputChange = (event, value, reason) => setIN_TXT(value);

    const filterOptions = (options, { inputValue }) =>
        matchSorter(options, inputValue, {
            keys: [`name${i18n.language === "pl" ? "" : `_${i18n.language}`}`, "indicator_id"],
            threshold: matchSorter.rankings.CONTAINS,
        });

    return (
        <>
            <div>
                {!!VALUES?.length && (
                    <Autocomplete
                        autoHighlight
                        id="search-indicator"
                        ref={inputRef}
                        onChange={(event, newValue, reason) => handleOnChange({ event, newValue, reason })}
                        onInputChange={(event, value, reason, a, b) => {
                            value?.length >= 0 && handleOnInputChange(event, value, reason, a, b);
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => `${translationFromJson(option, "name")}/${option.id}`}
                        options={IN_TXT ? VALUES : []}
                        noOptionsText={""}
                        PaperComponent={IN_TXT ? undefined : "span"}
                        fullWidth
                        popupIcon={
                            <Search fontSize={"small"} sx={{ width: 24, height: 24, fontSize: "1rem" /*18,*/ }} />
                        }
                        clearIcon={
                            <AppTooltip title={t("Wyczyść")} arrow placement={"top-end"} type={"dark"}>
                                <Clear
                                    fontSize={"small"}
                                    sx={{ width: 24, height: 24, fontSize: "1rem" /*18,*/ }}
                                    tabIndex={0}
                                />
                            </AppTooltip>
                        }
                        classes={{
                            endAdornment: classes.endAdornment,
                            input: classes.input,
                            inputFocused: clsx(classes.inputFocused, IN_TXT && classes.inputFocusedWithTxt),
                            listbox: classes.listbox,
                            option: classes.option,
                        }}
                        renderInput={(params) => {
                            // console.log(params.inputProps);
                            params.inputProps.value = params.inputProps.value.split("/")[0];
                            params.inputProps.value =
                                params.inputProps.value === "undefined" || !IN_TXT ? "" : params.inputProps.value;
                            return (
                                <TextField
                                    {...params}
                                    id={"search-datas"}
                                    label={t("Wyszukaj dane")}
                                    className={classes.TextFieldRoot}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: IN_TXT ? (
                                            <InputAdornment position="start">
                                                <Search fontSize={"small"} />
                                            </InputAdornment>
                                        ) : undefined,
                                        classes: { root: classes.outlinedInput },
                                    }}
                                    size={lgDown ? "small" : "medium"}
                                />
                            );
                        }}
                        disableClearable={!IN_TXT}
                        freeSolo={!!IN_TXT}
                        filterOptions={filterOptions}
                        renderOption={(props, option, { inputValue }) => {
                            const {key,...rest}=props
                            const highlightID = `${option.indicator_id}`?.replace(
                                new RegExp(inputValue, "gi"),
                                (str) => `<strong style="font-size: 125%;">${str}</strong>`
                            );

                            const highlightNameIdx = removeDiacritics(translationFromJson(option, "name"))
                                ?.toLocaleUpperCase()
                                .indexOf(inputValue?.toLocaleUpperCase());
                            let highlightNameN = translationFromJson(option, "name");
                            highlightNameN =
                                highlightNameIdx < 0
                                    ? highlightNameN
                                    : highlightNameN?.slice(0, highlightNameIdx) +
                                      `<strong style="font-weight: 600;" >${highlightNameN?.slice(
                                          highlightNameIdx,
                                          highlightNameIdx + inputValue?.length
                                      )}</strong>` +
                                      highlightNameN?.slice(
                                          highlightNameIdx + inputValue?.length,
                                          highlightNameN?.length
                                      );
                            return (
                                <li {...rest} key={key}>
                                    <div className={classes.listElement}>
                                        {showIDs ? (
                                            <sup dangerouslySetInnerHTML={{ __html: `${highlightID}` }} />
                                        ) : null}
                                        <span dangerouslySetInnerHTML={{ __html: `${highlightNameN}` }} />
                                    </div>
                                </li>
                            );
                        }}
                        componentsProps={{ clearIndicator: { "aria-label": t("Wyczyść"), title: "" } }}
                    />
                )}
            </div>
        </>
    );
};

export default SearchTree;
